import { type ReactNode, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({ open, onClose, title, description, children, size = 'md' }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // D17: stable id for aria-labelledby linkage between dialog and heading
  const titleId = useId();

  // BUGFIX: onClose was in this effect's deps, so any parent re-render that
  // created a fresh onClose closure (very common: `onClose={() => setX(false)}`
  // inline) caused the effect to re-run and autofocus the first focusable
  // again — which is the X close button in the header. Result: typing in
  // a form input would steal focus to the X button after every keystroke.
  //
  // Fix: capture onClose in a ref so the keydown handlers can read the
  // latest version without having `onClose` as a hook dependency. Effect
  // now only runs when `open` toggles, so autofocus fires exactly once on
  // open and never again until the modal is reopened.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
    window.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    // BUGFIX: dashboard <header> uses `bg-bg-primary/50 backdrop-blur`, and
    // `backdrop-filter` creates its own stacking context that bleeds through
    // semi-transparent modal backdrops as a thin frosted strip + bottom-border
    // line above the modal panel. Hiding the header entirely while a modal is
    // open is cleaner than darkening the backdrop (user explicitly rejected
    // "tambah jitamkan overlaynya bikin jelek"). The header's content (Live
    // clock, theme switcher) is non-essential during a focused modal task.
    document.body.classList.add('modal-open');

    // D17: autofocus the first focusable INPUT/TEXTAREA/SELECT inside the
    // dialog on open. Prefer form fields over the X close button so users
    // can start typing immediately without an extra Tab.
    const el = dialogRef.current;
    if (el) {
      const focusable =
        el.querySelector<HTMLElement>(
          'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])',
        ) ?? el.querySelector<HTMLElement>('button:not([disabled]):not([aria-label="Close"])');
      focusable?.focus();
    }

    // D17: basic Tab focus trap — keep focus within modal while open
    const trapFocus = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.closest('[hidden]'));
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', trapFocus);

    return () => {
      window.removeEventListener('keydown', onEsc);
      window.removeEventListener('keydown', trapFocus);
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [open]);

  if (!open) return null;

  const sizeClass = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }[size];

  // BUGFIX: when mounted as a descendant of <main> (the page content area),
  // the backdrop's `position: fixed` was rendering at y=24 instead of y=0.
  // Verified via Playwright: a control element appended directly to body
  // with the same CSS hit y=0 correctly, but our backdrop deep in the page
  // tree hit y=24 — leaving a 24px non-dimmed strip at the top that user
  // saw as "gap di atas tidak ikut overlay". Cause is some Chromium quirk
  // around `overflow-y-auto` ancestor (Layout's main content scroller) +
  // the modal's own `backdrop-filter` interaction. Fix: render through a
  // React portal directly into <body>, sidestepping any ancestor-induced
  // containing-block weirdness. Cleaner architecture too — modals at body
  // level is the standard pattern.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-4 pb-4 px-4 bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      {/* D17: role=dialog + aria-modal + aria-labelledby for screen readers */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={cn('cyber-panel w-full p-0 animate-slide-up max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col', sizeClass)}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || description) && (
          <div className="flex items-start justify-between p-5 border-b border-border-subtle">
            <div>
              {title && (
                <h2 id={titleId} className="text-lg font-display uppercase tracking-wider text-neon-cyan">
                  {title}
                </h2>
              )}
              {description && <p className="text-sm text-text-secondary mt-1">{description}</p>}
            </div>
            <button onClick={onClose} className="text-text-tertiary hover:text-neon-red transition-colors" aria-label="Close">
              <X size={20} />
            </button>
          </div>
        )}
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
